import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { Menu, MenuButton, MenuItems, Transition } from '@headlessui/react'
import clsx from 'clsx'
import { ActiveJobSummary, ActiveJobType } from 'graphql/generated/scheme'

const TYPE_LABELS: Record<ActiveJobType, string> = {
	IMPORT: 'Importing contacts',
	DELETE: 'Removing contacts',
	MESSAGE: 'Sending message',
	INVITE: 'Sending invites',
}

const TYPE_ICONS: Record<ActiveJobType, IconProp> = {
	IMPORT: ['fas', 'file-import'],
	DELETE: ['fas', 'trash'],
	MESSAGE: ['fas', 'paper-plane'],
	INVITE: ['fas', 'envelope-open-text'],
}

interface Props {
	jobs: ActiveJobSummary[]
	onOpen: (job: ActiveJobSummary) => void
}

export function ActiveJobsIndicator({ jobs, onOpen }: Props) {
	if (jobs.length === 0) return null

	return (
		<Menu as="div" className="relative inline-block">
			<MenuButton className="flex items-center gap-2 px-3 h-10 rounded-lg bg-accent-text/10 border border-accent-text/30 text-accent-text hover:bg-accent-text/20 transition-colors">
				<FontAwesomeIcon icon={['fas', 'spinner']} spin className="text-sm" />
				<span className="text-sm font-medium whitespace-nowrap">
					{jobs.length} {jobs.length === 1 ? 'job' : 'jobs'} running
				</span>
			</MenuButton>
			<Transition
				enter="transition ease-out duration-100"
				enterFrom="transform opacity-0 scale-95"
				enterTo="transform opacity-100 scale-100"
				leave="transition ease-in duration-75"
				leaveFrom="transform opacity-100 scale-100"
				leaveTo="transform opacity-0 scale-95"
			>
				<MenuItems className="absolute right-0 mt-2 w-80 origin-top-right rounded-xl bg-primary-surface border border-translucent-plus-border shadow-lg z-50 overflow-hidden focus:outline-none">
					<div className="px-4 py-2 border-b border-translucent-plus-border">
						<span className="text-xs font-semibold uppercase tracking-wider text-secondary-text">
							Background Jobs
						</span>
					</div>
					<div className="max-h-96 overflow-y-auto">
						{jobs.map(job => (
							<JobRow key={job.jobId} job={job} onOpen={onOpen} />
						))}
					</div>
				</MenuItems>
			</Transition>
		</Menu>
	)
}

function JobRow({ job, onOpen }: { job: ActiveJobSummary; onOpen: (job: ActiveJobSummary) => void }) {
	const percent = job.totalCount > 0 ? Math.round((job.processedCount / job.totalCount) * 100) : 0

	return (
		<button
			type="button"
			onClick={() => onOpen(job)}
			className="w-full px-4 py-3 hover:bg-white/[0.04] transition-colors text-left border-b border-translucent-plus-border last:border-b-0"
		>
			<div className="flex items-center gap-3">
				<FontAwesomeIcon icon={TYPE_ICONS[job.type]} className={clsx('text-sm text-accent-text')} />
				<div className="flex-1 min-w-0">
					<div className="flex items-center justify-between">
						<span className="text-sm font-medium text-primary-text truncate">{TYPE_LABELS[job.type]}</span>
						<span className="text-xs text-secondary-text flex-shrink-0 ml-2">{percent}%</span>
					</div>
					<div className="w-full h-1 bg-white/[0.08] rounded-full mt-2 overflow-hidden">
						<div
							className="h-full rounded-full transition-all duration-500 bg-accent-text"
							style={{ width: `${percent}%` }}
						/>
					</div>
					<div className="text-[11px] text-secondary-text mt-1">
						{job.processedCount.toLocaleString()} of {job.totalCount.toLocaleString()}
					</div>
				</div>
			</div>
		</button>
	)
}
